import { Routes, Route, useLocation } from 'react-router-dom'
import { useEffect } from 'react'

// Layout Components
import Header from './components/layout/Header'
import Footer from './components/layout/Footer'

// Page Components
import Home from './pages/Home'
import UserLogin from './pages/user/Login'
import UserRegister from './pages/user/Register'
import UserDashboard from './pages/user/Dashboard'
import UserCourses from './pages/user/Courses'
import UserCertificates from './pages/user/Certificates'
import UserProfile from './pages/user/Profile'
import DeleteProfile from './pages/user/DeleteProfile'
import UserWishlist from './pages/user/Wishlist'
import UserCart from './pages/user/Cart'
import UserCheckout from './pages/user/Checkout'
import UserLiveSessions from './pages/user/LiveSessions'
import UserPayments from './pages/user/Payments'
import CourseView from './pages/CourseView'
import CourseLearn from './pages/user/CourseLearn'
import PaymentSuccess from './pages/PaymentSuccess'
import RazorpayDemo from './pages/RazorpayDemo'
import VideoDemo from './pages/VideoDemo'
import PrivacyPolicy from './pages/PrivacyPolicy'
import TermsOfService from './pages/TermsOfService'
import Album from './pages/Album'

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard'
import AdminCourses from './pages/admin/Courses'
import CourseForm from './pages/admin/CourseForm'
import AdminStudents from './pages/admin/Students'
import AdminInstructors from './pages/admin/Instructors'
import AdminTestimonials from './pages/admin/Testimonials'
import AdminLiveSessions from './pages/admin/LiveSessions'
import LiveSessionForm from './pages/admin/LiveSessionForm'
import AdminPayments from './pages/admin/Payments'
import AdminSettings from './pages/admin/Settings'
import Discussions from './pages/admin/Discussions'
import AssignCourse from './pages/admin/AssignCourse'

// Layout Wrappers
import UserLayout from './components/layout/UserLayout'
import AdminLayout from './components/layout/AdminLayout'

// UI Components
import FloatingThemeToggle from './components/ui/FloatingThemeToggle'
import NoSSR from './components/ui/NoSSR'

function App() {
  const location = useLocation()

  // Theme initialization
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light'
    if (savedTheme === 'dark' || (savedTheme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [])

  // Simple scroll restoration for non-home pages
  useEffect(() => {
    if (location.pathname !== '/' && !location.hash) {
      window.scrollTo(0, 0)
    }
  }, [location.pathname])

  const isHomePage = location.pathname === '/'
  const isUserPage = location.pathname.startsWith('/user')
  const isAdminPage = location.pathname.startsWith('/admin')
  const isAuthPage = location.pathname === '/user/login' || location.pathname === '/user/register'
  const isStandalonePage = location.pathname === '/payment-success' || location.pathname === '/razorpay-demo'

  return (
    <div className="min-h-screen bg-white dark:bg-dark-900 transition-theme">
      {/* Header - Show on standalone pages, but not on auth pages or pages inside layouts */}
      {(isAuthPage || isStandalonePage) && <Header />}

      <main className="flex-1">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/user/login" element={<UserLogin />} />
          <Route path="/user/register" element={<UserRegister />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/razorpay-demo" element={<RazorpayDemo />} />
          <Route path="/video-demo" element={<VideoDemo />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/album" element={<Album />} />

          {/* User Routes with Layout */}
          <Route path="/user/*" element={<UserLayout />}>
            <Route path="dashboard" element={<UserDashboard />} />
            <Route path="courses" element={<UserCourses />} />
            <Route path="courses/:id" element={<CourseView />} />
            <Route path="courses/:id/learn" element={<CourseLearn />} />
            <Route path="live-sessions" element={<UserLiveSessions />} />
            <Route path="certificates" element={<UserCertificates />} />
            <Route path="wishlist" element={<UserWishlist />} />
            <Route path="cart" element={<UserCart />} />
            <Route path="checkout" element={<UserCheckout />} />
            <Route path="payments" element={<UserPayments />} />
            <Route path="dashboard/profile" element={<UserProfile />} />
            <Route path="delete-profile" element={<DeleteProfile />} />
          </Route>

          {/* Admin Routes with Layout */}
          <Route path="/admin/*" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="courses" element={<AdminCourses />} />
            <Route path="courses/new" element={<CourseForm />} />
            <Route path="courses/edit/:id" element={<CourseForm />} />
            <Route path="assign-course" element={<AssignCourse />} />
            <Route path="discussions" element={<Discussions />} />
            <Route path="students" element={<AdminStudents />} />
            <Route path="instructors" element={<AdminInstructors />} />
            <Route path="testimonials" element={<AdminTestimonials />} />
            <Route path="live-sessions" element={<AdminLiveSessions />} />
            <Route path="live-sessions/new" element={<LiveSessionForm />} />
            <Route path="live-sessions/edit/:id" element={<LiveSessionForm />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
        </Routes>
      </main>

      {/* Footer - Show ONLY on home page, NOT on user/admin pages or auth pages */}
      {isHomePage && <Footer />}

      {/* Floating Theme Toggle */}
      <NoSSR>
        <FloatingThemeToggle />
      </NoSSR>
    </div>
  )
}

export default App
